import { memo, useMemo } from "react";
import Widget from "./Widget";
import Loader from "ui-kit/src/Loader/Loader";
import { _ } from "i18n";
import { WidgetItem, MapType, InsightEventGroupEnum, InsightCategoryEnum } from "./constants";
import { EventFilterCondition, GPGamePlayerResponse } from "../../types";

interface WidgetListProps {
	activeTabWidgets: WidgetItem[];
	activeCategoryTab: InsightEventGroupEnum;
	activeGroupedEvents: Record<string, any>;
	activeCategoryEvents: any;
	periods: string[];
	filters: EventFilterCondition[];
	allPlayers: Record<number, GPGamePlayerResponse>;
	showPlayerNumber: boolean;
	isLimitedVisualization: boolean;
	handleDeleteWidget: (identifier: string | number) => void;
	handleMapTypeChange: (identifier: string | number, mapType: MapType) => void;
	getEventsForCategory: (category: InsightEventGroupEnum) => any;
	onWidgetRef?: (key: string, el: HTMLDivElement | null) => void;
}

const WidgetList = memo(
	({
		activeTabWidgets,
		activeCategoryTab,
		activeGroupedEvents,
		activeCategoryEvents,
		periods,
		filters,
		allPlayers,
		showPlayerNumber,
		isLimitedVisualization,
		handleDeleteWidget,
		handleMapTypeChange,
		getEventsForCategory,
		onWidgetRef,
	}: WidgetListProps) => {
		// Prepare widget data for the active category tab
		const widgetData = useMemo(() => {
			if (Object.keys(activeGroupedEvents).length === 0 || periods.length === 0) {
				return [];
			}
			const widgetsForTab = activeTabWidgets.filter((widget) => widget.group === activeCategoryTab && !widget.isPlaceholder);

			return widgetsForTab.map(({ eventType, mapType, isCustom, key, parameters, category }) => {
				let groupEvents: any[] = [];
				if (isCustom && category && (category as unknown as InsightEventGroupEnum) !== activeCategoryTab) {
					const customCategoryEvents = getEventsForCategory(category as unknown as InsightEventGroupEnum);
					groupEvents = customCategoryEvents?.[eventType] || [];
				} else {
					groupEvents = activeCategoryEvents[eventType] || [];
				}

				const widgetMapType =
					isLimitedVisualization && [MapType.PointMap, MapType.Directional, MapType.Zonal].includes(mapType as MapType)
						? MapType.Distribution
						: mapType;

				const widgetParameters =
					isLimitedVisualization && [MapType.PointMap, MapType.Directional, MapType.Zonal].includes(mapType as MapType) ? [] : parameters;

				return {
					key,
					eventType,
					parameters: widgetParameters,
					mapType: widgetMapType,
					isCustom,
					groupEvents,
					category,
				};
			});
		}, [activeTabWidgets, activeCategoryTab, activeCategoryEvents, getEventsForCategory, isLimitedVisualization, activeGroupedEvents, periods]);

		const widgets = useMemo(() => {
			return widgetData.map(({ key, eventType, parameters, mapType, isCustom, groupEvents, category }) => (
				<div
					key={key}
					ref={(el) => onWidgetRef?.(key, el)}
					className="widget-wrapper"
					style={{
						order: groupEvents.length === 0 ? 999 : 0,
						contentVisibility: "auto",
						containIntrinsicSize: "auto 500px",
					}}
				>
					<Widget
						eventType={eventType}
						parameters={parameters}
						filters={filters}
						events={groupEvents}
						players={allPlayers}
						showPlayerNumber={showPlayerNumber}
						mapType={mapType}
						customWidget={isCustom}
						onDeleteCustom={handleDeleteWidget}
						onMapTypeChange={handleMapTypeChange}
						isLimitedVisualization={isLimitedVisualization}
						identifier={key}
						category={category}
					/>
				</div>
			));
		}, [widgetData, filters, allPlayers, showPlayerNumber, handleDeleteWidget, handleMapTypeChange, isLimitedVisualization, onWidgetRef]);

		if (Object.keys(activeGroupedEvents).length === 0 || periods.length === 0) {
			return <Loader />;
		}

		if (widgets.length === 0) {
			return (
				<div className="insight-page-container__empty-state">
					<p>{_("gamePage.dataInsights.label.emptyWidget")}</p>
				</div>
			);
		}

		return <>{widgets}</>;
	},
	(prevProps, nextProps) => {
		if (
			prevProps.activeTabWidgets.length !== nextProps.activeTabWidgets.length ||
			prevProps.activeCategoryTab !== nextProps.activeCategoryTab ||
			prevProps.periods.length !== nextProps.periods.length ||
			prevProps.showPlayerNumber !== nextProps.showPlayerNumber ||
			prevProps.isLimitedVisualization !== nextProps.isLimitedVisualization
		) {
			return false;
		}
		if (JSON.stringify(prevProps.filters) !== JSON.stringify(nextProps.filters)) {
			return false;
		}
		// Re-render when event data changes (e.g. events load: same keys, new arrays). Compare per-key lengths.
		const prevLengths = Object.fromEntries(
			Object.entries(prevProps.activeCategoryEvents).map(([k, v]) => [k, (v as unknown[])?.length ?? 0])
		);
		const nextLengths = Object.fromEntries(
			Object.entries(nextProps.activeCategoryEvents).map(([k, v]) => [k, (v as unknown[])?.length ?? 0])
		);
		if (JSON.stringify(prevLengths) !== JSON.stringify(nextLengths)) {
			return false;
		}
		return true;
	}
);

export default WidgetList;
